import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  GET_MALL_SETTINGS, UPDATE_MALL_SETTINGS, GET_CURRENT_USER,
  type ManageMallSettingsData, type ManageCurrentUser,
} from '../../graphql/management'
import { useAuth } from '../../auth/AuthContext'
import { Feedback, useFormFeedback, type UserError } from './Feedback'

// Mall-wide settings. Read returns a nested `settings` JSON to seed the form;
// write uses flat scalar args (the iOS read-JSON / write-flat asymmetry).
// v1: name, description, currency, feature toggles. Commission + policies are
// follow-ups (commission has a fraction/percent direction to verify).
const CURRENCIES = ['GBP', 'USD', 'EUR']

function readToggle(settings: Record<string, unknown> | null | undefined, ...keys: string[]): boolean {
  if (!settings) return false
  const features = (settings.features as Record<string, unknown> | undefined) ?? {}
  for (const k of keys) {
    if (settings[k] === true) return true
    if (features[k] === true) return true
  }
  return false
}
function readStr(settings: Record<string, unknown> | null | undefined, key: string, fallback = ''): string {
  const v = settings?.[key]
  return typeof v === 'string' ? v : fallback
}

export default function MallSettings() {
  const { user } = useAuth()
  const fb = useFormFeedback()
  const { data: meData } = useQuery<ManageCurrentUser>(GET_CURRENT_USER, { skip: !!user?.mallId })
  const mallId = user?.mallId ?? meData?.currentUser?.mall?.id ?? null

  const { data, loading } = useQuery<ManageMallSettingsData>(GET_MALL_SETTINGS, {
    variables: { mallId }, skip: !mallId, fetchPolicy: 'cache-and-network',
  })
  const [save, { loading: saving }] = useMutation(UPDATE_MALL_SETTINGS)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [currency, setCurrency] = useState('GBP')
  const [livestreaming, setLivestreaming] = useState(false)
  const [chat, setChat] = useState(false)
  const [analytics, setAnalytics] = useState(false)

  const mall = data?.mallById
  useEffect(() => {
    if (!mall) return
    setName(mall.name)
    setDescription(mall.description ?? '')
    setCurrency(readStr(mall.settings, 'default_currency', readStr(mall.settings, 'currency', 'GBP')))
    setLivestreaming(readToggle(mall.settings, 'livestreaming_enabled', 'livestreaming'))
    setChat(readToggle(mall.settings, 'chat_enabled', 'chat'))
    setAnalytics(readToggle(mall.settings, 'analytics_enabled', 'analytics'))
  }, [mall])

  if (!mallId) return <div className="empty-state"><h2>No mall selected</h2><p className="muted">Your account isn’t linked to a mall.</p></div>
  if (loading && !mall) return <div className="muted">Loading mall settings…</div>

  async function onSave(e: React.FormEvent) {
    e.preventDefault(); fb.reset()
    try {
      const { data } = await save({
        variables: {
          mallId, name, description, defaultCurrency: currency,
          livestreamingEnabled: livestreaming, chatEnabled: chat, analyticsEnabled: analytics,
        },
      })
      if (!fb.fromUserErrors(data?.updateMallSettings?.userErrors as UserError[] | undefined)) return
      fb.setSuccess('Mall settings saved.')
    } catch (err) { fb.setError(err instanceof Error ? err.message : String(err)) }
  }

  return (
    <div className="manage-page">
      <header className="manage-header"><h1>Mall settings</h1></header>
      <form className="create-form" onSubmit={onSave}>
        <label><span>Mall name</span><input value={name} onChange={(e) => setName(e.target.value)} required /></label>
        <label><span>Description</span><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} /></label>
        <label><span>Default currency</span>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <fieldset className="toggle-group">
          <label className="toggle"><input type="checkbox" checked={livestreaming} onChange={(e) => setLivestreaming(e.target.checked)} /><span>Livestreaming enabled</span></label>
          <label className="toggle"><input type="checkbox" checked={chat} onChange={(e) => setChat(e.target.checked)} /><span>Chat enabled</span></label>
          <label className="toggle"><input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} /><span>Analytics enabled</span></label>
        </fieldset>
        <Feedback error={fb.error} success={fb.success} />
        <div className="form-actions">
          <button className="btn-primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save mall settings'}</button>
        </div>
      </form>
    </div>
  )
}
